/**
 * `<caos-data-loader>` — the shape of a bulk load: what, onto which object, how, and what each row did.
 * (Catalog: Developer surfaces › DataLoader — compose a bulk insert, upsert, update or delete, map the columns, and read the per-row result.)
 *
 * A DEPENDENT CHOICE IS SHOWN EMPTY, NOT HIDDEN AND NOT DISABLED. The external-identifier field only
 * exists for an upsert, and it has no options until an object is chosen. The platform's picker had
 * no notion of one choice deciding another's contents, and the two obvious answers are both wrong: a
 * hidden control makes the screen change shape under somebody and leaves them unable to see that the
 * choice exists; a disabled one says "not for you" rather than "not yet". So it is present, empty,
 * with its prompt readable, saying what it is waiting for.
 *
 * DELETE TAKES NO MAPPING. A delete is driven by ids, so the mapping section is not merely unused
 * for it — it is meaningless, and drawing an empty one invites somebody to wonder what they got
 * wrong. The section is replaced by a line saying what a delete actually needs.
 *
 * MAPPING IS PROPOSED, NEVER IMPOSED. Columns are matched to fields by name because that is right
 * most of the time, and every proposal is a control the person can change — including to "do not
 * load this column", which is a real answer and not the absence of one. A proposal the person cannot
 * see is a guess that becomes their data.
 *
 * A ROW THAT FAILED KEEPS ITS OWN ROW NUMBER. "Row 412 failed" is the only thing that connects an
 * error back to the file on somebody's disk; renumbering the failures 1..9 throws it away.
 *
 * Styling seams: `::part(loader)`, `::part(settings)`, `::part(field)`, `::part(mapping)`,
 * `::part(mapping-row)`, `::part(summary)`, `::part(results)`, `::part(start)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosTableRow } from './data-table.js';

/** What the load does. */
export const LOADER_ACTIONS = ['insert', 'upsert', 'update', 'delete'] as const;
export type CaosLoaderAction = (typeof LOADER_ACTIONS)[number];

/** How it is sent — the bulk path, or the synchronous one. */
export const LOADER_SIZINGS = ['batch', 'standard'] as const;
export type CaosLoaderSizing = (typeof LOADER_SIZINGS)[number];

/** The value meaning "do not load this column" — a real answer, not the absence of one. */
export const MAPPING_SKIP = '';

/** One row's outcome. */
export interface CaosLoaderRowResult {
  /** The row's number IN THE FILE, 1-based. Never renumbered. */
  readonly row: number;
  readonly ok: boolean;
  /** The record id, for a row that worked. */
  readonly id?: string;
  /** What went wrong, for a row that did not. */
  readonly error?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .loader { display: flex; flex-direction: column; gap: var(--caos-space-4); }

  .settings { display: flex; gap: var(--caos-space-3); flex-wrap: wrap; }

  .field { display: flex; flex-direction: column; gap: 3px; min-width: 150px; }
  .field > span {
    font-family: var(--caos-font);
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
  }
  .field select {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .field select:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  /* Waiting, not forbidden: it keeps full contrast and its prompt says what it is waiting for. */
  .field[data-waiting="yes"] select { border-style: dashed; color: var(--caos-color-text-muted); }

  .mapping { display: flex; flex-direction: column; gap: var(--caos-space-2); }
  .mapping-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
    gap: var(--caos-space-2);
    align-items: center;
  }
  .mapping-row .col {
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mapping-row .arrow { color: var(--caos-color-text-ghost); text-align: center; }
  .mapping-row select {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 4px 8px;
    min-width: 0;
  }
  .mapping-row select:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .mapping-row[data-skipped="yes"] .col { color: var(--caos-color-text-ghost); text-decoration: line-through; }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary .failed { color: var(--caos-color-danger-text); font-weight: 600; }

  .note { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); font-style: italic; }

  .start {
    align-self: flex-start;
    border: 1px solid var(--caos-color-accent);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-family: var(--caos-font);
    font-size: 13px;
    font-weight: 600;
    padding: 6px 16px;
    cursor: pointer;
  }
  .start:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .start:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

/**
 * Propose a column-to-field mapping by name.
 *
 * Case and the usual separators are ignored, because a file column called `Account Name` and a field
 * called `AccountName` are the same intent. Anything with no confident match is proposed as SKIP
 * rather than guessed at — a wrong mapping nobody notices writes the wrong data into the wrong field,
 * which is worse than a column somebody has to set by hand.
 */
export function proposeMapping(columns: readonly string[], fields: readonly string[]): Record<string, string> {
  const flatten = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const byFlat = new Map(fields.map((f) => [flatten(f), f]));
  const out: Record<string, string> = {};
  for (const column of columns) out[column] = byFlat.get(flatten(column)) ?? MAPPING_SKIP;
  return out;
}

export class CaosDataLoader extends CaosElement {
  static get observedAttributes(): string[] {
    return ['action', 'sizing', 'object', 'external-id', 'running', 'start-label'];
  }

  private settingsEl: HTMLDivElement;
  private mappingEl: HTMLDivElement;
  private summaryEl: HTMLParagraphElement;
  private resultsEl: CaosDataTable;
  private startEl: HTMLButtonElement;

  private cols: readonly string[] = [];
  private fieldNames: readonly string[] = [];
  private objectNames: readonly string[] = [];
  private externalIdFields: readonly string[] = [];
  private map: Record<string, string> = {};
  private rowResults: readonly CaosLoaderRowResult[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'loader';
    wrap.setAttribute('part', 'loader');

    this.settingsEl = document.createElement('div');
    this.settingsEl.className = 'settings';
    this.settingsEl.setAttribute('part', 'settings');

    this.mappingEl = document.createElement('div');
    this.mappingEl.className = 'mapping';
    this.mappingEl.setAttribute('part', 'mapping');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.resultsEl = document.createElement('caos-data-table') as CaosDataTable;
    this.resultsEl.setAttribute('part', 'results');
    this.resultsEl.hidden = true;

    this.startEl = document.createElement('button');
    this.startEl.type = 'button';
    this.startEl.className = 'start';
    this.startEl.setAttribute('part', 'start');
    this.startEl.addEventListener('click', () => this.start());

    wrap.append(this.settingsEl, this.mappingEl, this.summaryEl, this.startEl, this.resultsEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, before: string | null, after: string | null): void {
    if (name === 'object' && before !== after) {
      // A different object means different fields, so every proposal made against the old one is
      // now a mapping onto fields that may not exist. Re-proposing is the honest move; keeping them
      // would silently carry a mapping nobody chose.
      this.map = proposeMapping(this.cols, this.fieldNames);
    }
    this.render();
  }

  get action(): CaosLoaderAction {
    const asked = this.getAttribute('action');
    return (LOADER_ACTIONS as readonly string[]).includes(asked ?? '') ? (asked as CaosLoaderAction) : 'insert';
  }

  get sizing(): CaosLoaderSizing {
    return this.getAttribute('sizing') === 'standard' ? 'standard' : 'batch';
  }

  /** The objects that can be loaded onto. */
  get objects(): readonly string[] {
    return this.objectNames;
  }

  set objects(next: readonly string[]) {
    this.objectNames = Array.isArray(next) ? next : [];
    this.render();
  }

  /** The file's columns, in file order. */
  get columns(): readonly string[] {
    return this.cols;
  }

  set columns(next: readonly string[]) {
    this.cols = Array.isArray(next) ? next : [];
    this.map = proposeMapping(this.cols, this.fieldNames);
    this.render();
  }

  /** The fields of the chosen object, which the columns map onto. */
  get fields(): readonly string[] {
    return this.fieldNames;
  }

  set fields(next: readonly string[]) {
    this.fieldNames = Array.isArray(next) ? next : [];
    this.map = proposeMapping(this.cols, this.fieldNames);
    this.render();
  }

  /**
   * The fields that can serve as an external identifier.
   *
   * EMPTY until an object is chosen, and that emptiness is shown rather than hidden. See the header:
   * this is the dependent choice the platform's own picker could not express.
   */
  get externalIds(): readonly string[] {
    return this.externalIdFields;
  }

  set externalIds(next: readonly string[]) {
    this.externalIdFields = Array.isArray(next) ? next : [];
    this.render();
  }

  /** Column → field. A column mapped to {@link MAPPING_SKIP} is deliberately not loaded. */
  get mapping(): Readonly<Record<string, string>> {
    return { ...this.map };
  }

  set mapping(next: Readonly<Record<string, string>>) {
    this.map = { ...next };
    this.render();
  }

  /** What each row did. */
  get results(): readonly CaosLoaderRowResult[] {
    return this.rowResults;
  }

  set results(next: readonly CaosLoaderRowResult[]) {
    this.rowResults = Array.isArray(next) ? next : [];
    this.render();
  }

  /** The rows that failed, keeping their own row numbers. */
  get failures(): readonly CaosLoaderRowResult[] {
    return this.rowResults.filter((r) => !r.ok);
  }

  /** Whether the load is described completely enough to be asked for. */
  get ready(): boolean {
    if (this.getAttribute('object') === null || this.getAttribute('object') === '') return false;
    if (this.cols.length === 0) return false;
    if (this.action === 'upsert' && (this.getAttribute('external-id') ?? '') === '') return false;
    // A delete needs ids, not a mapping; anything else needs at least one column actually going
    // somewhere, or the load is a request to write nothing.
    if (this.action !== 'delete' && Object.values(this.map).every((f) => f === MAPPING_SKIP)) return false;
    return true;
  }

  private start(): void {
    if (!this.ready || this.hasAttribute('running')) return;
    this.dispatchEvent(
      new CustomEvent('caos-load-start', {
        bubbles: true,
        composed: true,
        detail: {
          action: this.action,
          sizing: this.sizing,
          object: this.getAttribute('object'),
          // OMITTED, not set to undefined, for an action that has no external identifier. This
          // component's whole convention is that an absent thing is absent rather than present and
          // empty, and the two are distinguishable to a caller: `'externalId' in detail` and a
          // round trip through JSON both tell them apart.
          ...(this.action === 'upsert' ? { externalId: this.getAttribute('external-id') } : {}),
          // Only the columns actually going somewhere. Handing over the skipped ones would make the
          // caller re-apply a decision this component already recorded.
          mapping: Object.fromEntries(Object.entries(this.map).filter(([, f]) => f !== MAPPING_SKIP)),
        },
      }),
    );
  }

  private render(): void {
    this.renderSettings();
    this.renderMapping();
    this.renderResults();

    const running = this.hasAttribute('running');
    this.startEl.textContent = running ? 'Loading…' : (this.getAttribute('start-label') ?? 'Start load');
    this.startEl.disabled = running || !this.ready;
  }

  private renderSettings(): void {
    const parts: HTMLElement[] = [
      this.choice('Action', 'action', [...LOADER_ACTIONS], this.action, null),
      this.choice('Object', 'object', [...this.objectNames], this.getAttribute('object') ?? '', 'Choose an object'),
      this.choice('Sizing', 'sizing', [...LOADER_SIZINGS], this.sizing, null),
    ];

    if (this.action === 'upsert') {
      // Present and EMPTY when there is nothing yet to choose from — not hidden, which makes the
      // screen change shape under somebody, and not disabled, which says "not for you" rather than
      // "not yet".
      const waiting = this.externalIdFields.length === 0;
      parts.push(
        this.choice(
          'External identifier',
          'external-id',
          [...this.externalIdFields],
          this.getAttribute('external-id') ?? '',
          waiting ? 'Choose an object first' : 'Choose a field',
          waiting,
        ),
      );
    }

    this.settingsEl.replaceChildren(...parts);
  }

  private choice(
    label: string,
    attr: string,
    values: readonly string[],
    current: string,
    prompt: string | null,
    waiting = false,
  ): HTMLElement {
    const field = document.createElement('label');
    field.className = 'field';
    field.setAttribute('part', 'field');
    if (waiting) field.setAttribute('data-waiting', 'yes');
    const text = document.createElement('span');
    text.textContent = label;
    const select = document.createElement('select');
    select.setAttribute('aria-label', label);
    if (prompt !== null) {
      const o = document.createElement('option');
      o.value = '';
      o.textContent = prompt;
      o.selected = current === '';
      select.append(o);
    }
    for (const v of values) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = v;
      o.selected = v === current;
      select.append(o);
    }
    select.addEventListener('change', () => {
      if (select.value === '') this.removeAttribute(attr);
      else this.setAttribute(attr, select.value);
      this.dispatchEvent(
        new CustomEvent('caos-load-change', {
          bubbles: true,
          composed: true,
          detail: { action: this.action, sizing: this.sizing, object: this.getAttribute('object') },
        }),
      );
    });
    field.append(text, select);
    return field;
  }

  private renderMapping(): void {
    if (this.action === 'delete') {
      const note = document.createElement('p');
      note.className = 'note';
      // Not an empty mapping section: a delete has no columns to map, and drawing an unused one
      // invites somebody to wonder what they got wrong.
      note.textContent = 'A delete is driven by record ids. The file needs an id column and nothing else.';
      this.mappingEl.replaceChildren(note);
      return;
    }

    if (this.cols.length === 0) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent = 'Choose a file to map its columns onto fields.';
      this.mappingEl.replaceChildren(note);
      return;
    }

    this.mappingEl.replaceChildren(
      ...this.cols.map((column) => {
        const row = document.createElement('div');
        row.className = 'mapping-row';
        row.setAttribute('part', 'mapping-row');
        const chosen = this.map[column] ?? MAPPING_SKIP;
        if (chosen === MAPPING_SKIP) row.setAttribute('data-skipped', 'yes');

        const col = document.createElement('span');
        col.className = 'col';
        col.textContent = column;
        col.title = column;

        const arrow = document.createElement('span');
        arrow.className = 'arrow';
        arrow.textContent = '→';
        arrow.setAttribute('aria-hidden', 'true');

        const select = document.createElement('select');
        select.setAttribute('aria-label', `Field for the column ${column}`);
        const skip = document.createElement('option');
        skip.value = MAPPING_SKIP;
        // A real answer with real words. "Do not load" is a decision; a blank option is an omission.
        skip.textContent = 'Do not load this column';
        skip.selected = chosen === MAPPING_SKIP;
        select.append(skip);
        for (const field of this.fieldNames) {
          const o = document.createElement('option');
          o.value = field;
          o.textContent = field;
          o.selected = field === chosen;
          select.append(o);
        }
        select.addEventListener('change', () => {
          this.map = { ...this.map, [column]: select.value };
          this.dispatchEvent(
            new CustomEvent('caos-load-mapping', {
              bubbles: true,
              composed: true,
              detail: { column, field: select.value, mapping: this.mapping },
            }),
          );
        });

        row.append(col, arrow, select);
        return row;
      }),
    );
  }

  private renderResults(): void {
    const nodes: Node[] = [];
    if (this.rowResults.length > 0) {
      const failed = this.failures.length;
      const ok = this.rowResults.length - failed;
      const strong = document.createElement('strong');
      strong.textContent = `${ok} of ${this.rowResults.length} rows loaded`;
      nodes.push(strong);
      if (failed > 0) {
        nodes.push(document.createTextNode(' · '));
        const f = document.createElement('span');
        f.className = 'failed';
        f.textContent = `${failed} failed`;
        nodes.push(f);
      }
    } else {
      const mapped = Object.values(this.map).filter((f) => f !== MAPPING_SKIP).length;
      if (this.action !== 'delete' && this.cols.length > 0) {
        const strong = document.createElement('strong');
        strong.textContent = `${mapped} of ${this.cols.length} columns mapped`;
        nodes.push(strong);
      }
    }
    this.summaryEl.replaceChildren(...nodes);

    this.resultsEl.hidden = this.rowResults.length === 0;
    if (this.rowResults.length === 0) return;

    this.resultsEl.columns = [
      { key: 'row', label: 'Row', align: 'end', width: '10%' },
      { key: 'outcome', label: 'Outcome', width: '18%' },
      { key: 'detail', label: 'Detail' },
    ];
    // Failures first: a per-row table of four thousand rows is read for the nine that did not work.
    const ordered = [...this.rowResults].sort((a, b) => (a.ok === b.ok ? a.row - b.row : a.ok ? 1 : -1));
    this.resultsEl.rows = ordered.map(
      (r) =>
        ({
          id: String(r.row),
          cells: {
            // The row's number IN THE FILE. It is the only thing connecting an error back to the
            // file on somebody's disk.
            row: r.row,
            outcome: r.ok ? 'loaded' : 'failed',
            detail: r.ok ? (r.id ?? '') : (r.error ?? 'no error was reported'),
          },
        }) as CaosTableRow,
    );
  }
}

define('caos-data-loader', CaosDataLoader);

declare global {
  interface HTMLElementTagNameMap {
    'caos-data-loader': CaosDataLoader;
  }
}
